/** A (possibly nested) tree of translated strings, as held by one locale JSON file. */
export type Translations = { [key: string]: string | Translations };

/** i18next namespaces assembled from per-feature locale files. */
export const NAMESPACES = ['common', 'settings'] as const;

export type Namespace = (typeof NAMESPACES)[number];
export type LocaleResources = Record<'ar' | 'en' | 'fr', Record<Namespace, Translations>>;

/** One locale file: its path (for error messages) and parsed JSON. */
export type LocaleFile = { content: Translations; path: string };

/**
 * Matches `./locales/<lng>/<ns>/<key.path>.json`. The file name is the dotted key path whose contents
 * the file holds: `activation.json` is the `activation` object, `pages.vehicles.json` is `pages.vehicles`.
 */
const LOCALE_FILE_PATH = /^\.\/locales\/([^/]+)\/([^/]+)\/(.+)\.json$/;

function isTranslations(value: string | Translations | undefined): value is Translations {
  return typeof value === 'object' && value !== null;
}

/**
 * Merges `source` into `target`, throwing if any leaf is defined twice. Two files may both contribute to
 * the same parent object (`pages.vehicles.json` and `pages.workOrders.json` both feed `pages`), but never to
 * the same key: a silent last-one-wins would hide a mistake that shows up only as a wrong string at runtime.
 */
function mergeInto(target: Translations, source: Translations, keyPath: string[], filePath: string): void {
  for (const [key, value] of Object.entries(source)) {
    const existing = target[key];
    const fullPath = [...keyPath, key].join('.');
    if (existing === undefined) {
      // Clone: later files merge into this object, which must never be the imported JSON module itself.
      target[key] = structuredClone(value);
    } else if (isTranslations(existing) && isTranslations(value)) {
      mergeInto(existing, value, [...keyPath, key], filePath);
    } else {
      throw new Error(`Duplicate i18n key "${fullPath}" (defined again in ${filePath})`);
    }
  }
}

/**
 * Assembles one namespace from its per-feature files. Each file holds the contents of the key path named
 * by its file name; the result is the deep merge of all of them. Throws on a duplicated key.
 */
export function mergeLocaleFiles(files: LocaleFile[]): Translations {
  const merged: Translations = {};
  for (const { content, path } of files) {
    const fileName = path.slice(path.lastIndexOf('/') + 1).replace(/\.json$/, '');
    const keyPath = fileName.split('.');
    // Wrap the contents in its key path, then merge: this is what makes `pages.vehicles.json` land at
    // `pages.vehicles` and lets the duplicate check cover every file the same way.
    const wrapped = keyPath.reduceRight<Translations>((inner, key) => ({ [key]: inner }), content);
    mergeInto(merged, wrapped, [], path);
  }
  return merged;
}

/**
 * Groups `./locales/<lng>/<ns>/*.json` modules by language and namespace and merges each group.
 * A missing language or namespace yields an empty tree rather than an error: the parity spec is what
 * reports a locale that lacks a file.
 */
export function buildResources(modules: Record<string, Translations>): LocaleResources {
  const grouped = new Map<string, LocaleFile[]>();
  for (const [path, content] of Object.entries(modules)) {
    const match = LOCALE_FILE_PATH.exec(path);
    if (!match) {
      continue;
    }
    const groupKey = `${match[1]}/${match[2]}`;
    grouped.set(groupKey, [...(grouped.get(groupKey) ?? []), { content, path }]);
  }

  const namespacesOf = (language: string): Record<Namespace, Translations> => {
    const merge = (namespace: Namespace): Translations =>
      mergeLocaleFiles((grouped.get(`${language}/${namespace}`) ?? []).sort((x, y) => x.path.localeCompare(y.path)));
    return { common: merge('common'), settings: merge('settings') };
  };

  return { ar: namespacesOf('ar'), en: namespacesOf('en'), fr: namespacesOf('fr') };
}

/**
 * Every feature area keeps its own JSON file per language and namespace under
 * `locales/<lng>/<ns>/`, so two features adding keys never touch the same file. Vite inlines the glob at
 * build time (and in Vitest), so this is as static as the JSON imports it replaced.
 */
export const localeModules = import.meta.glob<Translations>('./locales/*/*/*.json', {
  eager: true,
  import: 'default',
});

export const resources: LocaleResources = buildResources(localeModules);

export const { common: arCommon, settings: arSettings } = resources.ar;
export const { common: enCommon, settings: enSettings } = resources.en;
export const { common: frCommon, settings: frSettings } = resources.fr;

/** Reads the node at a dotted key path, e.g. `pages.workOrders.list`. Returns `undefined` when absent. */
export function translationAt(tree: Translations, keyPath: string): string | Translations | undefined {
  return keyPath.split('.').reduce<string | Translations | undefined>((node, key) => {
    return isTranslations(node) ? node[key] : undefined;
  }, tree);
}

/** Like {@link translationAt}, but for a node that must be an object of translations; throws otherwise. */
export function subtree(tree: Translations, keyPath: string): Translations {
  const node = translationAt(tree, keyPath);
  if (!isTranslations(node)) {
    throw new Error(`No i18n subtree at "${keyPath}"`);
  }
  return node;
}
